import { TeamRow, SportType, QualRule, formatPointValue, getTeamRankColor } from "@/hooks/useSportsStandings";

interface StandingsTableProps {
  displayRows: TeamRow[];
  sportTab: SportType;
  leagueTab: string;
  isLoading: boolean;
  rulesData: Record<string, QualRule>;
}

interface TableColumn {
  key: string;
  label: string;
  className: string;
}

function getTableColumns(sport: SportType): TableColumn[] {
  if (sport === "축구") {
    return [
      { key: "rank", label: "순위", className: "w-10 text-center" },
      { key: "team", label: "팀명", className: "text-left min-w-[120px]" },
      { key: "games", label: "경기", className: "w-10 text-center" },
      { key: "wins", label: "승", className: "w-8 text-center" },
      { key: "draws", label: "무", className: "w-8 text-center" },
      { key: "losses", label: "패", className: "w-8 text-center" },
      { key: "goalsFor", label: "득점", className: "w-10 text-center" },
      { key: "goalsAgainst", label: "실점", className: "w-10 text-center" },
      { key: "goalDiff", label: "득실차", className: "w-12 text-center" },
      { key: "points", label: "승점", className: "w-12 text-center" },
    ];
  }
  return [
    { key: "rank", label: "순위", className: "w-10 text-center" },
    { key: "team", label: "팀명", className: "text-left min-w-[120px]" },
    { key: "games", label: "경기", className: "w-10 text-center" },
    { key: "wins", label: "승", className: "w-8 text-center" },
    { key: "draws", label: "무", className: "w-8 text-center" },
    { key: "losses", label: "패", className: "w-8 text-center" },
    { key: "points", label: sport === "배구" ? "승점" : "승률", className: "w-14 text-center font-bold" },
    { key: "goalDiff", label: "득실차", className: "w-12 text-center" },
  ];
}

function getCellValue(team: TeamRow, key: string, sport: SportType): string {
  if (key === "points") return formatPointValue(team.points, sport);
  if (key === "goalDiff") {
    const val = team.goalDiff;
    if (val > 0) return `+${val}`;
    return String(val);
  }
  if (key === "team") return team.team;
  return String((team as Record<string, unknown>)[key] ?? "");
}

function getRowLeftBorder(rank: number, league: string, rules: Record<string, QualRule>): string {
  const rule = rules[league];
  if (!rule) return "";
  if (rank <= rule.cl && rule.cl > 0) return "border-l-2 border-l-blue-500";
  if (rule.el > rule.cl && rank <= rule.el) return "border-l-2 border-l-orange-500";
  if (rule.relStart > 0 && rank >= rule.relStart) return "border-l-2 border-l-red-500";
  return "";
}

export default function StandingsTable({ displayRows, sportTab, leagueTab, isLoading, rulesData }: StandingsTableProps) {
  const columns = getTableColumns(sportTab);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-8 h-8 rounded-full border-2 border-background-200 border-t-primary-500 animate-spin" />
        <p className="mt-3 text-sm text-foreground-600">순위 데이터를 불러오는 중...</p>
      </div>
    );
  }

  if (displayRows.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-foreground-600">
        <span className="w-8 h-8 flex items-center justify-center mx-auto mb-2 text-foreground-600">
          <i className="ri-calendar-close-line text-2xl" />
        </span>
        <p className="mt-2">현재 시즌 순위가 없습니다</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px] md:text-xs border-collapse min-w-[600px]">
        <thead>
          <tr className="bg-background-50 border-b border-background-300 text-foreground-600">
            {columns.map((col) => (
              <th key={col.key} className={`px-2 py-2.5 font-medium ${col.className}`}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayRows.map((team) => {
            const rankColorClass = getTeamRankColor(team.rank, leagueTab, rulesData || {});
            const leftBorder = getRowLeftBorder(team.rank, leagueTab, rulesData || {});

            return (
              <tr
                key={team.rank}
                className={`border-b border-background-300/50 hover:bg-background-200/50 transition-colors ${leftBorder}`}
              >
                {columns.map((col) => {
                  if (col.key === "rank") {
                    return (
                      <td key={col.key} className={`px-2 py-2.5 font-bold ${rankColorClass} ${col.className}`}>
                        {team.rank}
                      </td>
                    );
                  }
                  if (col.key === "team") {
                    return (
                      <td key={col.key} className={`px-2 py-2.5 ${col.className}`}>
                        <div className="flex items-center gap-2">
                          <img
                            alt={team.team}
                            className="w-5 h-5 rounded-full object-contain flex-shrink-0"
                            src={team.logo}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='12' r='10' fill='%23e5e7eb'/%3E%3C/svg%3E";
                            }}
                          />
                          <span className="text-foreground-600 truncate">{team.team}</span>
                        </div>
                      </td>
                    );
                  }
                  const val = getCellValue(team, col.key, sportTab);
                  return (
                    <td key={col.key} className={`px-2 py-2.5 text-foreground-600 ${col.className}`}>
                      {val}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}